import { useEditorStore } from "@/lib/stores/editor-store/editor-store";
import { useMemo, useState } from "react";
import {
  useSourceImagesByOriginVideoIdQuery,
  usePagedSourceImagesQuery,
  useDeleteSourceImagesMutation,
} from "@/lib/queries/source-image-queries";
import {
  useAnimatedImagesByOriginVideoIdQuery,
  usePagedAnimatedImagesQuery,
  useDeleteAnimatedImagesMutation,
} from "@/lib/queries/animated-image-queries";
import { Button } from "@/lib/components/Button/Button";
import {
  AssetSelectorBody,
  AssetSelectorBottomBar,
  AssetSelectorContent,
  AssetSelectorDialog,
  AssetSelectorDropzoneOverlay,
  AssetSelectorHeader,
  AssetSelectorLayout,
  AssetSelectorMediaCard,
  AssetSelectorSidebar,
  MediaGrid,
} from "./AssetSelectorDialog/AssetSelectorDialog";
import GradientTabButton from "@/lib/components/Button/GradientTabButton";
import {
  IconPhoto,
  IconTrash,
  IconUpload,
  IconVideo,
} from "@tabler/icons-react";
import { useDropzone } from "react-dropzone";
import Toast from "@/lib/components/Toast";
import { ImageUploadArea } from "./ImageUpload";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/lib/components/Dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/lib/components/Select";
import { cn } from "@/lib/utils";
import { SourceImageDto } from "@repo/api/source-image";
import {
  AnimatedImageDto,
  AnimatedImageWithThumbnailDto,
} from "@repo/api/animated-image";

function shouldShowAnimatedImage(
  img: Pick<AnimatedImageDto, "type" | "status">,
) {
  return img.type === "UPLOADED" || img.status === "COMPLETED";
}

export function SourceImageSelectorDialog() {
  const [selectedSourceImages, setSelectedSourceImages] = useState<
    SourceImageDto[]
  >([]);
  const [selectedAnimatedImages, setSelectedAnimatedImages] = useState<
    AnimatedImageDto[]
  >([]);
  const addSourceImagesToComposition = useEditorStore(
    (state) => state.service.addSourceImagesToComposition,
  );
  const addAnimatedImagesToComposition = useEditorStore(
    (state) => state.service.addAnimatedImagesToComposition,
  );
  const setIsImageSelectorDialogOpen = useEditorStore(
    (state) => state.service.setIsImageSelectorDialogOpen,
  );
  const isOpen = useEditorStore(
    (state) => state.sourceImageSelectorDialogState.isOpen,
  );

  const handleSelectSourceImage = (sourceImage: SourceImageDto) => {
    setSelectedSourceImages((prev) =>
      prev.some((img) => img.externalId === sourceImage.externalId)
        ? prev.filter((img) => img.externalId !== sourceImage.externalId)
        : [...prev, sourceImage],
    );
  };

  const handleSelectAnimatedImage = (animatedImage: AnimatedImageDto) => {
    setSelectedAnimatedImages((prev) =>
      prev.some((img) => img.externalId === animatedImage.externalId)
        ? prev.filter((img) => img.externalId !== animatedImage.externalId)
        : [...prev, animatedImage],
    );
  };

  const handleConfirm = () => {
    if (
      selectedSourceImages.length === 0 &&
      selectedAnimatedImages.length === 0
    ) {
      return;
    }

    if (selectedSourceImages.length > 0) {
      addSourceImagesToComposition(selectedSourceImages);
    }

    if (selectedAnimatedImages.length > 0) {
      addAnimatedImagesToComposition(selectedAnimatedImages);
    }

    setSelectedSourceImages([]);
    setSelectedAnimatedImages([]);
    setIsImageSelectorDialogOpen(false);
  };

  const handleUpload = async (uploaded: SourceImageDto[]) => {
    setSelectedSourceImages((prev) => [...prev, ...uploaded]);
  };

  const handleUploadAnimated = async (uploaded: AnimatedImageDto[]) => {
    setSelectedAnimatedImages((prev) => [...prev, ...uploaded]);
  };

  const handleOnClear = () => {
    setSelectedSourceImages([]);
    setSelectedAnimatedImages([]);
  };

  return (
    <AssetSelectorDialog
      open={isOpen}
      onOpenChange={setIsImageSelectorDialogOpen}
    >
      <AssetSelectorLayout>
        <SourceImageSelector
          mode="add"
          selectedSourceImages={selectedSourceImages}
          selectedAnimatedImages={selectedAnimatedImages}
          handleSelectSourceImage={handleSelectSourceImage}
          handleSelectAnimatedImage={handleSelectAnimatedImage}
          handleUpload={handleUpload}
          handleUploadAnimated={handleUploadAnimated}
          handleConfirm={handleConfirm}
          handleOnClear={handleOnClear}
        />
      </AssetSelectorLayout>
    </AssetSelectorDialog>
  );
}

export function SourceImageReplaceDialog({
  isOpen,
  setIsOpen,
  handleSelect,
}: {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  handleSelect: (sourceImage: SourceImageDto) => void;
}) {
  return (
    <AssetSelectorDialog open={isOpen} onOpenChange={setIsOpen}>
      <AssetSelectorLayout>
        <SourceImageSelector
          mode="replace"
          handleSelectSourceImage={handleSelect}
        />
      </AssetSelectorLayout>
    </AssetSelectorDialog>
  );
}

type AddModeProps = {
  mode: "add";
  selectedSourceImages: SourceImageDto[];
  selectedAnimatedImages: AnimatedImageDto[];
  handleSelectAnimatedImage: (animatedImage: AnimatedImageDto) => void;
  handleUpload: (uploaded: SourceImageDto[]) => void;
  handleUploadAnimated: (uploaded: AnimatedImageDto[]) => void;
  handleConfirm: () => void;
  handleOnClear: () => void;
};

type ReplaceModeProps = {
  mode: "replace";
  selectedSourceImages?: never;
  selectedAnimatedImages?: never;
  handleSelectAnimatedImage?: never;
  handleUpload?: never;
  handleUploadAnimated?: never;
  handleConfirm?: never;
  handleOnClear?: never;
};

type ModeProps = AddModeProps | ReplaceModeProps;

type TabOption = "ALL_IMAGES" | "ALL_ANIMATIONS";

type ScopeOption = "CURRENT_VIDEO" | "ALL";

function SourceImageSelector({
  selectedSourceImages = [],
  selectedAnimatedImages = [],
  mode,
  handleSelectSourceImage,
  handleSelectAnimatedImage,
  handleUpload,
  handleUploadAnimated,
  handleConfirm,
  handleOnClear,
}: {
  handleSelectSourceImage: (sourceImage: SourceImageDto) => void;
} & ModeProps) {
  const [tab, setTab] = useState<TabOption>("ALL_IMAGES");
  const [scope, setScope] = useState<ScopeOption>("CURRENT_VIDEO");
  const [isUploading, setIsUploading] = useState(false);
  const isAddMode = mode === "add";
  const teamExternalId = useEditorStore((state) => state.teamExternalId);
  const videoExternalId = useEditorStore((state) => state.videoExternalId);
  const compositionSourceImages = useEditorStore((state) => state.sourceImages);
  const compositionAnimatedImages = useEditorStore(
    (state) => state.animatedImages,
  );
  const uploadSourceImages = useEditorStore(
    (state) => state.service.uploadSourceImages,
  );
  const uploadAnimatedImages = useEditorStore(
    (state) => state.service.uploadAnimatedImages,
  );

  const isCurrentVideoScope = scope === "CURRENT_VIDEO";

  const {
    data: pagedSourceImagesData,
    isLoading: isLoadingAllImages,
    hasNextPage: hasNextPageImages,
    fetchNextPage: fetchNextPageImages,
    isFetchingNextPage: isFetchingNextPageImages,
  } = usePagedSourceImagesQuery(teamExternalId);

  const allSourceImages = useMemo(
    () =>
      pagedSourceImagesData?.pages.flatMap((page) => page.sourceImages) ?? [],
    [pagedSourceImagesData],
  );

  const {
    data: currentVideoSourceImages = [],
    isLoading: isLoadingCurrentVideoImages,
  } = useSourceImagesByOriginVideoIdQuery(teamExternalId, videoExternalId, {
    enabled: isCurrentVideoScope,
  });

  const visibleSourceImages = useMemo(() => {
    if (!isCurrentVideoScope) return allSourceImages;
    const map = new Map<string, SourceImageDto>();
    for (const img of currentVideoSourceImages) {
      map.set(img.externalId, img);
    }
    for (const img of Object.values(compositionSourceImages)) {
      if (img.deletedAt) continue;
      if (map.has(img.externalId)) continue;
      map.set(img.externalId, img);
    }
    return Array.from(map.values());
  }, [
    isCurrentVideoScope,
    allSourceImages,
    currentVideoSourceImages,
    compositionSourceImages,
  ]);

  const {
    data: pagedAnimatedImagesData,
    isLoading: isLoadingAllAnimations,
    hasNextPage: hasNextPageAnimations,
    fetchNextPage: fetchNextPageAnimations,
    isFetchingNextPage: isFetchingNextPageAnimations,
  } = usePagedAnimatedImagesQuery(teamExternalId);

  const allAnimatedImages = useMemo(
    () =>
      pagedAnimatedImagesData?.pages.flatMap((page) => page.animatedImages) ??
      [],
    [pagedAnimatedImagesData],
  );

  const {
    data: currentVideoAnimatedImages = [],
    isLoading: isLoadingCurrentVideoAnimations,
  } = useAnimatedImagesByOriginVideoIdQuery(teamExternalId, videoExternalId, {
    enabled: isCurrentVideoScope,
  });

  const visibleAnimatedImages = useMemo(() => {
    if (!isCurrentVideoScope) {
      return allAnimatedImages.filter(shouldShowAnimatedImage);
    }
    const map = new Map<string, AnimatedImageWithThumbnailDto>();
    for (const img of currentVideoAnimatedImages) {
      if (!shouldShowAnimatedImage(img)) continue;
      map.set(img.externalId, img);
    }
    for (const img of Object.values(compositionAnimatedImages)) {
      if (img.deletedAt) continue;
      if (!shouldShowAnimatedImage(img)) continue;
      if (map.has(img.externalId)) continue;
      const thumbnailSrc =
        compositionSourceImages[img.startImageExternalId]?.thumbnailSrc ?? "";
      map.set(img.externalId, { ...img, thumbnailSrc });
    }
    return Array.from(map.values());
  }, [
    isCurrentVideoScope,
    allAnimatedImages,
    currentVideoAnimatedImages,
    compositionAnimatedImages,
    compositionSourceImages,
  ]);

  const { getRootProps, getInputProps, open, isDragActive } = useDropzone({
    noClick: true,
    noKeyboard: true,
    disabled: isUploading,
    accept: isAddMode
      ? {
          "image/png": [".png"],
          "image/jpeg": [".jpg", ".jpeg"],
          "image/webp": [".webp"],
          "video/mp4": [".mp4"],
          "video/quicktime": [".mov"],
          "video/webm": [".webm"],
        }
      : {
          "image/png": [".png"],
          "image/jpeg": [".jpg", ".jpeg"],
          "image/webp": [".webp"],
        },
    onDrop: async (acceptedFiles) => {
      if (isUploading || acceptedFiles.length === 0) return;

      const images = acceptedFiles.filter((file) =>
        file.type.startsWith("image/"),
      );
      const videos = isAddMode
        ? acceptedFiles.filter((file) => file.type.startsWith("video/"))
        : [];

      if (images.length === 0 && videos.length === 0) return;

      setIsUploading(true);
      try {
        let uploadedImages: SourceImageDto[] = [];
        let uploadedAnimated: AnimatedImageDto[] = [];

        if (images.length > 0) {
          uploadedImages = await uploadSourceImages(images);
        }
        if (videos.length > 0) {
          uploadedAnimated = await uploadAnimatedImages(videos);
        }

        if (uploadedImages.length > 0 && handleUpload) {
          handleUpload(uploadedImages);
        }
        if (uploadedAnimated.length > 0 && handleUploadAnimated) {
          handleUploadAnimated(uploadedAnimated);
        }

        const hasImages = uploadedImages.length > 0;
        const hasAnimated = uploadedAnimated.length > 0;
        if (hasImages && !hasAnimated && tab !== "ALL_IMAGES") {
          setTab("ALL_IMAGES");
        } else if (hasAnimated && !hasImages && tab !== "ALL_ANIMATIONS") {
          setTab("ALL_ANIMATIONS");
        }
      } catch (err) {
        console.error(err);
        Toast.error("Failed to upload files");
      } finally {
        setIsUploading(false);
      }
    },
  });

  const isLoading =
    tab === "ALL_IMAGES"
      ? isCurrentVideoScope
        ? isLoadingCurrentVideoImages
        : isLoadingAllImages
      : isCurrentVideoScope
        ? isLoadingCurrentVideoAnimations
        : isLoadingAllAnimations;

  const isElementsEmpty =
    tab === "ALL_IMAGES"
      ? visibleSourceImages.length === 0
      : visibleAnimatedImages.length === 0;

  const totalSelectedCount =
    selectedSourceImages.length + selectedAnimatedImages.length;

  const selectedLabel = (() => {
    const parts: string[] = [];
    const imageCount = selectedSourceImages.length;
    const animationCount = selectedAnimatedImages.length;

    if (imageCount > 0) {
      parts.push(`${imageCount} ${imageCount === 1 ? "image" : "images"}`);
    }
    if (animationCount > 0) {
      parts.push(
        `${animationCount} ${animationCount === 1 ? "animation" : "animations"}`,
      );
    }

    return `${parts.join(" and ")} selected`;
  })();

  const showBorder = isElementsEmpty && !isLoading && tab === "ALL_IMAGES";

  return (
    <>
      <AssetSelectorHeader
        title={mode === "add" ? "Images & Animations" : "Images"}
        isElementsEmpty={showBorder}
      />
      <AssetSelectorBody>
        <AssetSelectorSidebar isElementsEmpty={showBorder}>
          <Tabs
            mode={mode}
            onUploadClick={open}
            isUploading={isUploading}
            selectedTab={tab}
            onAllImagesClick={() => setTab("ALL_IMAGES")}
            onAllAnimationsClick={() => setTab("ALL_ANIMATIONS")}
          />
          <Filters scope={scope} onScopeChange={setScope} />
        </AssetSelectorSidebar>
        <AssetSelectorContent>
          <div className="relative min-h-0 flex-1" {...getRootProps()}>
            <input {...getInputProps()} />
            {tab === "ALL_IMAGES" ? (
              <MediaGrid
                key={scope + tab}
                items={visibleSourceImages}
                isLoading={isLoading}
                hideEmptyState={isDragActive}
                emptyState={
                  <ImageUploadArea
                    getInputProps={getInputProps}
                    openImageUpload={open}
                    isDragActive={isDragActive}
                    isImagesEmpty={isElementsEmpty}
                    variant="dialog"
                  />
                }
                hasNextPage={!isCurrentVideoScope && hasNextPageImages}
                isFetchingNextPage={isFetchingNextPageImages}
                fetchNextPage={fetchNextPageImages}
                getItemKey={(sourceImage) => sourceImage.externalId}
                renderItem={(sourceImage) => (
                  <AssetSelectorMediaCard
                    src={sourceImage.src}
                    thumbnailSrc={sourceImage.thumbnailSrc}
                    isSelected={
                      isAddMode &&
                      selectedSourceImages.some(
                        (img) => img.externalId === sourceImage.externalId,
                      )
                    }
                    onSelect={() => handleSelectSourceImage(sourceImage)}
                    showSelectIcon={isAddMode}
                    coverFit
                  />
                )}
              />
            ) : (
              <MediaGrid
                key={scope + tab}
                items={visibleAnimatedImages}
                isLoading={isLoading}
                hideEmptyState={isDragActive}
                emptyState={
                  <div className="flex h-full flex-1 flex-col items-center justify-center px-8 py-12">
                    <span className="text-md mb-3 font-medium text-text-primary">
                      No animations yet
                    </span>
                    <p className="text-center text-sm leading-6 font-medium text-text-secondary">
                      Animations you create will appear here. <br />
                      Try adjusting the filters to see more results.
                    </p>
                  </div>
                }
                hasNextPage={!isCurrentVideoScope && hasNextPageAnimations}
                isFetchingNextPage={isFetchingNextPageAnimations}
                fetchNextPage={fetchNextPageAnimations}
                getItemKey={(animatedImage) => animatedImage.externalId}
                renderItem={(animatedImage) => (
                  <AssetSelectorMediaCard
                    src={animatedImage.forwardSrc}
                    thumbnailSrc={animatedImage.thumbnailSrc}
                    isSelected={selectedAnimatedImages.some(
                      (img) => img.externalId === animatedImage.externalId,
                    )}
                    onSelect={() => handleSelectAnimatedImage?.(animatedImage)}
                    showSelectIcon
                    mediaType={
                      animatedImage.status === "COMPLETED" ? "video" : "image"
                    }
                    coverFit
                  />
                )}
              />
            )}
            {(isUploading || isDragActive) && (
              <AssetSelectorDropzoneOverlay
                isUploadingTitle="Uploading files..."
                description="to add them to the library"
                isUploading={isUploading}
                isElementsEmpty={isElementsEmpty}
              />
            )}
          </div>
          {isAddMode && totalSelectedCount > 0 && (
            <AssetSelectorBottomBar
              selectedCount={totalSelectedCount}
              selectedLabel={selectedLabel}
              onClear={handleOnClear}
              className={showBorder ? "border-l" : undefined}
            >
              {totalSelectedCount > 0 && (
                <DeleteAssetsDialog
                  selectedSourceImageExternalIds={selectedSourceImages.map(
                    (img) => img.externalId,
                  )}
                  selectedAnimatedImageExternalIds={selectedAnimatedImages.map(
                    (img) => img.externalId,
                  )}
                  onDeleted={handleOnClear}
                />
              )}
              <Button size="sm" onClick={handleConfirm}>
                Add Selected
              </Button>
            </AssetSelectorBottomBar>
          )}
        </AssetSelectorContent>
      </AssetSelectorBody>
    </>
  );
}

function Filters({
  scope,
  onScopeChange,
}: {
  scope: ScopeOption;
  onScopeChange: (scope: ScopeOption) => void;
}) {
  return (
    <div className="flex-1 p-6">
      <h2 className="mb-5 font-semibold text-text-primary">Filters</h2>
      <div className="flex flex-col gap-4">
        <div className="relative">
          <Select
            value={scope}
            onValueChange={(value) => onScopeChange(value as ScopeOption)}
          >
            <SelectTrigger
              className={cn(
                "w-full",
                scope !== "CURRENT_VIDEO" && "text-text-secondary",
              )}
            >
              <SelectValue>
                {scope === "CURRENT_VIDEO" ? "Current Video" : "All"}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">All</SelectItem>
              <SelectItem value="CURRENT_VIDEO">Current Video</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  );
}

function Tabs({
  mode,
  onUploadClick,
  isUploading,
  onAllImagesClick,
  onAllAnimationsClick,
  selectedTab,
}: {
  mode: "add" | "replace";
  onUploadClick: () => void;
  isUploading: boolean;
  onAllImagesClick: () => void;
  onAllAnimationsClick: () => void;
  selectedTab: TabOption;
}) {
  return (
    <div className="flex flex-col gap-3 border-b border-grey-200 p-6">
      <GradientTabButton
        icon={IconPhoto}
        label="Images"
        onClick={onAllImagesClick}
        isActive={selectedTab === "ALL_IMAGES"}
      />
      {mode === "add" && (
        <GradientTabButton
          icon={IconVideo}
          label="Animations"
          onClick={onAllAnimationsClick}
          isActive={selectedTab === "ALL_ANIMATIONS"}
        />
      )}
      <GradientTabButton
        icon={IconUpload}
        label={
          isUploading
            ? "Uploading..."
            : mode === "add"
              ? "Upload file"
              : "Upload image"
        }
        onClick={onUploadClick}
        isActive={false}
        disabled={isUploading}
      />
    </div>
  );
}

function DeleteAssetsDialog({
  selectedSourceImageExternalIds,
  selectedAnimatedImageExternalIds,
  onDeleted,
}: {
  selectedSourceImageExternalIds: string[];
  selectedAnimatedImageExternalIds: string[];
  onDeleted: () => void;
}) {
  const [open, setOpen] = useState(false);
  const teamExternalId = useEditorStore((state) => state.teamExternalId);
  const setSourceImagesAsDeleted = useEditorStore(
    (state) => state.service.setSourceImagesAsDeleted,
  );
  const setAnimatedImagesAsDeleted = useEditorStore(
    (state) => state.service.setAnimatedImagesAsDeleted,
  );

  const imageCount = selectedSourceImageExternalIds.length;
  const animationCount = selectedAnimatedImageExternalIds.length;

  const { mutateAsync: deleteSourceImages, isPending: isDeletingSourceImages } =
    useDeleteSourceImagesMutation({ teamExternalId });

  const {
    mutateAsync: deleteAnimatedImages,
    isPending: isDeletingAnimatedImages,
  } = useDeleteAnimatedImagesMutation({ teamExternalId });

  const isDeleting = isDeletingSourceImages || isDeletingAnimatedImages;

  const handleDelete = async () => {
    try {
      await Promise.all([
        imageCount > 0
          ? deleteSourceImages(selectedSourceImageExternalIds)
          : Promise.resolve(),
        animationCount > 0
          ? deleteAnimatedImages(selectedAnimatedImageExternalIds)
          : Promise.resolve(),
      ]);
    } catch {
      return;
    }

    if (imageCount > 0) {
      setSourceImagesAsDeleted(selectedSourceImageExternalIds);
    }
    if (animationCount > 0) {
      setAnimatedImagesAsDeleted(selectedAnimatedImageExternalIds);
    }

    setOpen(false);
    onDeleted();
  };

  const parts: string[] = [];
  if (imageCount > 0) {
    parts.push(`${imageCount} ${imageCount === 1 ? "image" : "images"}`);
  }
  if (animationCount > 0) {
    parts.push(
      `${animationCount} ${animationCount === 1 ? "animation" : "animations"}`,
    );
  }
  const summary = parts.join(" and ");

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="iconSm">
          <IconTrash />
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader className="w-full">
          <DialogTitle>Delete Selected</DialogTitle>
          <DialogDescription>
            Are you sure you want to delete the selected {summary} from the
            library?
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="mt-0">
          <Button onClick={handleDelete} disabled={isDeleting}>
            {isDeleting ? "Deleting..." : "Delete"}
          </Button>
          <DialogClose asChild>
            <Button type="button" variant="outline" disabled={isDeleting}>
              Cancel
            </Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
